import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMediaQuery } from 'react-responsive';
import WidgetVisibilityToggle from './WidgetVisibilityToggle';

/**
 * Sortable widget wrapper component
 * Wraps individual widgets to make them draggable within their section
 */
const SortableWidget = ({
  id,
  widgetName,
  children,
  isDragEnabled,
  isVisible,
  onToggleVisibility,
  showTitleBar = false,
  onExpand,
  size = 'medium',
  titleAccessory = null,
}) => {
  const isMobileDashboard = useMediaQuery({ maxWidth: 767 });
  const [mobileCollapsed, setMobileCollapsed] = useState(false);

  useEffect(() => {
    setMobileCollapsed(Boolean(isMobileDashboard && showTitleBar));
  }, [isMobileDashboard, showTitleBar]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: !isDragEnabled,
  });

  // Keep our own handle on the widget element while still wiring dnd-kit's ref.
  const widgetRef = useRef(null);
  const setRefs = useCallback(
    (node) => {
      widgetRef.current = node;
      setNodeRef(node);
    },
    [setNodeRef],
  );

  // Skip the scroll-into-view on the initial collapsed/expanded sync (mount).
  const didMountRef = useRef(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isMobileBodyCollapsed = isMobileDashboard && showTitleBar && mobileCollapsed;

  // When a widget body is expanded on mobile, scroll just enough so the bottom
  // of this widget clears the fixed settings button (instead of a blind nudge).
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!isMobileDashboard || mobileCollapsed || !widgetRef.current) return;

    const button = document.querySelector('.dashboard-settings-button');
    if (!button) return;

    const GAP = 16; // breathing room between the widget and the button
    const widgetBottom = widgetRef.current.getBoundingClientRect().bottom;
    const buttonTop = button.getBoundingClientRect().top;
    const overflow = widgetBottom - (buttonTop - GAP);

    if (overflow > 0) {
      window.scrollBy({ top: overflow, behavior: 'smooth' });
    }
  }, [mobileCollapsed]);

  return (
    <div
      ref={setRefs}
      style={style}
      className={clsx(
        'sortable-widget',
        `widget-size-${size}`,
        isDragging && 'dragging',
        showTitleBar && 'has-title-bar',
        isMobileBodyCollapsed && 'sortable-widget--mobile-collapsed',
      )}
      {...attributes}
      {...listeners}
    >
      {isDragEnabled && (
        <WidgetVisibilityToggle
          widgetName={widgetName}
          isVisible={isVisible}
          onToggle={onToggleVisibility}
        />
      )}
      {showTitleBar && (
        <div className="widget-title-bar">
          {isMobileDashboard ? (
            <button
              type="button"
              className="widget-title-bar__collapse-trigger"
              onClick={() => setMobileCollapsed((c) => !c)}
              aria-expanded={!mobileCollapsed}
            >
              <span
                className={clsx(
                  'widget-title-bar__chevron-icon',
                  !mobileCollapsed && 'widget-title-bar__chevron-icon--open',
                )}
                aria-hidden
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.5 5.25 7 8.75 10.5 5.25"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <h3 className="widget-title-bar__title">
                {widgetName}
                {titleAccessory && (
                  <span className="widget-title-bar__title-accessory"> {titleAccessory}</span>
                )}
              </h3>
            </button>
          ) : (
            <h3 className="widget-title-bar__title">
              {widgetName}
              {titleAccessory && (
                <span className="widget-title-bar__title-accessory"> {titleAccessory}</span>
              )}
            </h3>
          )}

          {onExpand && (
            <button
              type="button"
              className="widget-title-bar__expand-button"
              onClick={(e) => {
                e.stopPropagation();
                onExpand();
              }}
              aria-label={`Expand ${widgetName}`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 14L14 2M14 2H6M14 2V10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      )}
      <div
        className={clsx(showTitleBar && 'widget-content')}
        hidden={isMobileBodyCollapsed || undefined}
      >
        <div className={`widget-content__body widget-content__body--${id}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default SortableWidget;
